import {
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  from,
  fromEvent,
  map,
  Observable,
  of,
  startWith,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { MenuController } from '@ionic/angular';
import * as moment from 'moment-timezone';

import { Location as LocationModel } from '@shared/location/models';
import { AuthRestaurantService, SocketService } from '@tablesready/frontend-store';
import {
  Inbound,
  Party,
  PartySource,
  PartyTypes,
  sourceToStringMap
} from '@shared/party';
import { hasUnpaidCheckout } from '@shared/party/functions';
import { AuthUser } from '@shared/signup/models';
import { WebSocketEventsEnum } from '@shared/websocket';
import { AppIcon, AppIconColor, AppIconSize } from '@tablesready/frontend-icons';
import { FrontendBreakpointsService } from '@tablesready/frontend-breakpoints';
import { FrontendKeyboardService } from '@tablesready/frontend-keyboard';
import { NotificationCenterService } from './notification-center.service';
import { PricePlansRestrictionsService } from '@tablesready/frontend-restrictions';
import { PartyNavigationService } from '@tablesready/frontend-party-info-compact';

const enum NotificationType {
  message = 'message',
  notification = 'notification',
}

interface NotificationTypeConfig {
  getList: () => Party[];
  setList: (value: Party[]) => void;
  getOffset: () => number;
  setOffset: (value: number) => void;
  getHasMore: () => boolean;
  setHasMore: (value: boolean) => void;
  getIsLoading: () => boolean;
  setIsLoading: (value: boolean) => void;
  loadFn: (locationId: string, pageSize: number, offset: number) => Observable<Party[]>;
  markAsReadFn: (id: string) => Observable<unknown>;
  markAllAsReadFn: (restaurantId: string) => Observable<unknown>;
  emitter?: EventEmitter<Party>;
}

const BASE_SIDEBAR_WIDTH = 400;
const HOLD_DURATION_MS = 900;
const RESIZE_DEBOUNCE_MS = 200;
const WS_DEBOUNCE_MS = 1000;
const PAGE_SIZE = 50;
const SCROLL_LOAD_THRESHOLD_PX = 50;
const MENU_ID = 'notification-center';

@Component({
  selector: 'tablesready-notification-center-sidebar',
  templateUrl: './notification-center-sidebar.component.html',
  styleUrls: ['./notification-center-sidebar.component.scss'],
  standalone: false,
  animations: [
    trigger('markAsRead', [
      transition(':leave', [
        animate('200ms ease-out', style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class NotificationCenterSidebarComponent implements OnInit, OnDestroy {
  @Output() messageClicked = new EventEmitter<Party>();

  showMessagesTab = false;
  showNotificationsTab = false;
  private isStarterPlan = false;
  private isBusinessPlan = false;

  tabs: string[] = [];
  selectedTab = '';
  messagesCount$: Observable<number> | null = null;
  notificationsCount$: Observable<number> | null = null;

  isMobile = true;
  isShortHeight = false;
  isKeyboardOpen = false;
  chatWithParty?: Party;
  messages: Party[] = [];
  notifications: Party[] = [];
  location?: LocationModel;
  editPartyMethod?: (party: Party) => void;

  user: AuthUser | null = null;
  private readonly subscriptions = new Subscription();
  baseSidebarWidth = BASE_SIDEBAR_WIDTH;
  private messagesOffset = 0;
  private notificationsOffset = 0;

  hasMoreMessages = true;
  hasMoreNotifications = true;
  isLoadingMoreMessages = false;
  isLoadingMoreNotifications = false;
  isChatOpen = false;
  currentSidebarWidth = this.baseSidebarWidth;
  fillProgress = 0;
  fillTransitionMs = 0;
  private holdTimer: ReturnType<typeof setTimeout> | null = null;

  protected readonly icon = AppIcon;
  protected readonly iconSize = AppIconSize;
  protected readonly iconColor = AppIconColor;

  private get notificationTypeStrategies(): Record<NotificationType, NotificationTypeConfig> {
    return {
      [NotificationType.message]: {
        getList: (): Party[] => this.messages,
        setList: (v): void => {
          this.messages = v;
        },
        getOffset: (): number => this.messagesOffset,
        setOffset: (v): void => {
          this.messagesOffset = v;
        },
        getHasMore: (): boolean => this.hasMoreMessages,
        setHasMore: (v): void => {
          this.hasMoreMessages = v;
        },
        getIsLoading: (): boolean => this.isLoadingMoreMessages,
        setIsLoading: (v): void => {
          this.isLoadingMoreMessages = v;
        },
        loadFn: (loc, size, offset) => this.notificationCenterService.getMessages(loc, size, offset),
        markAsReadFn: partyId => this.notificationCenterService.markMessageAsRead(partyId),
        markAllAsReadFn: restaurantId => this.notificationCenterService.markAllMessagesAsRead(restaurantId),
        emitter: this.messageClicked,
      },
      [NotificationType.notification]: {
        getList: (): Party[] => this.notifications,
        setList: (v): void => {
          this.notifications = v;
        },
        getOffset: (): number => this.notificationsOffset,
        setOffset: (v): void => {
          this.notificationsOffset = v;
        },
        getHasMore: (): boolean => this.hasMoreNotifications,
        setHasMore: (v): void => {
          this.hasMoreNotifications = v;
        },
        getIsLoading: (): boolean => this.isLoadingMoreNotifications,
        setIsLoading: (v): void => {
          this.isLoadingMoreNotifications = v;
        },
        loadFn: (loc, size, offset) => this.notificationCenterService.getNotifications(loc, size, offset),
        markAsReadFn: id => this.notificationCenterService.markNotificationAsRead(id),
        markAllAsReadFn: restaurantId => this.notificationCenterService.markAllNotificationsAsRead(restaurantId),
      },
    };
  }

  private readonly partyNavigationService = inject(PartyNavigationService);
  private readonly keyboardService = inject(FrontendKeyboardService);

  constructor(
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly socketService: SocketService,
    private readonly notificationCenterService: NotificationCenterService,
    private readonly menuController: MenuController,
    private readonly pricePlansRestrictionsService: PricePlansRestrictionsService,
    private readonly breakpointsService: FrontendBreakpointsService,
  ) {
  }

  get isCompactHeader(): boolean {
    return this.isMobile || this.isShortHeight;
  }

  onChangeNav(changeTo: string): void {
    this.openChat(false);
    this.selectedTab = changeTo;
  }

  ngOnInit(): void {
    this.setSidebarWidth(this.baseSidebarWidth);
    this.initializeCounters();
    this.subscribeToAuthUser();
    this.subscribeToWebSocketEvents();

    const resizeSub = fromEvent(window, 'resize')
      .pipe(
        debounceTime(RESIZE_DEBOUNCE_MS),
        map(() => window.innerWidth),
        startWith(window.innerWidth),
        distinctUntilChanged(),
        tap((windowWidth: number) => {
          this.openChat(false);
          this.closeSidebar();

          if (windowWidth < this.baseSidebarWidth) {
            this.baseSidebarWidth = windowWidth;
          }

          if (this.baseSidebarWidth < BASE_SIDEBAR_WIDTH && windowWidth > BASE_SIDEBAR_WIDTH) {
            this.baseSidebarWidth = BASE_SIDEBAR_WIDTH;
          }

          this.currentSidebarWidth = this.baseSidebarWidth;
          this.isMobile = windowWidth < this.baseSidebarWidth * 2;
        }),
      )
      .subscribe();

    this.subscriptions.add(resizeSub);

    const shortHeightSub = this.breakpointsService.observeShortHeight()
      .subscribe((isShort) => {
        this.isShortHeight = isShort;
      });
    this.subscriptions.add(shortHeightSub);

    const keyboardSub = this.keyboardService.keyboardVisible$
      .subscribe((visible) => {
        this.isKeyboardOpen = visible;
      });
    this.subscriptions.add(keyboardSub);
  }

  ngOnDestroy(): void {
    this.cancelHold();
    this.subscriptions.unsubscribe();
  }

  onMenuWillOpen(): void {
    this.resetPaginationState();
    this.loadMessages(true);
    this.loadNotifications(true);
  }

  onMenuDidClose(): void {
    this.messages = [];
    this.notifications = [];
    this.openChat(false);
  }

  openChatArrow: () => void = () => {
    this.openChat(false);
  };

  openChat(isOpen: boolean, party?: Party): void {
    let sidebarWidth = this.baseSidebarWidth;

    if (isOpen && !this.isMobile) {
      sidebarWidth = this.baseSidebarWidth * 2;
    }

    this.setSidebarWidth(sidebarWidth);
    this.isChatOpen = isOpen;

    if (!isOpen && this.chatWithParty) {
      this.handleMarkAsRead(this.chatWithParty, NotificationType.message, {
        emitEvent: false,
        removeFromList: true,
      });
      this.chatWithParty = undefined;
      return;
    }

    this.chatWithParty = party;
  }

  openParty(party: Party): void {
    this.handleMarkAsRead(party, NotificationType.notification);
    this.partyNavigationService.navigateToPartyPage(party);
  }

  closeSidebar(): void {
    this.openChat(false);
    void this.menuController.close(MENU_ID);
  }

  markAsReadMessage(party: Party): void {
    this.handleMarkAsRead(party, NotificationType.message, { removeFromList: true });
  }

  markAsReadNotification(party: Party): void {
    this.handleMarkAsRead(party, NotificationType.notification, { removeFromList: true });
  }

  markAllMessagesAsRead(): void {
    this.handleMarkAllAsRead(NotificationType.message);
  }

  markAllNotificationsAsRead(): void {
    this.handleMarkAllAsRead(NotificationType.notification);
  }

  startHold(type: 'messages' | 'notifications'): void {
    // Reset to 0 with no transition first so the fill starts clean
    this.fillProgress = 0;
    this.fillTransitionMs = 0;

    // Next frame: start the CSS transition to 100%
    requestAnimationFrame(() => {
      this.fillProgress = 100;
      this.fillTransitionMs = HOLD_DURATION_MS;
    });

    this.holdTimer = setTimeout(() => {
      this.stopFill();
      if (type === 'notifications') {
        this.markAllNotificationsAsRead();
      } else {
        this.markAllMessagesAsRead();
      }
    }, HOLD_DURATION_MS);
  }

  cancelHold(): void {
    this.stopFill();
  }

  private stopFill(): void {
    if (this.holdTimer !== null) {
      clearTimeout(this.holdTimer);
      this.holdTimer = null;
    }
    // Remove transition before resetting so it snaps instantly
    this.fillTransitionMs = 0;
    this.fillProgress = 0;
  }

  onContentScroll(event: Event): void {
    const el = event.target as HTMLElement | null;
    if (!el) {
      return;
    }

    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceToBottom > SCROLL_LOAD_THRESHOLD_PX) {
      return;
    }

    const type = this.getActiveNotificationType();
    if (type !== null) {
      this.loadData(type);
    }
  }

  getUnreadMessages(party: Party): Inbound[] {
    if (!party.inbounds) {
      return [];
    }

    return party.inbounds
      .filter(inbound => !inbound.isRead)
      .sort((a, b) => b.time - a.time);
  }

  getNotificationText(party: Party): string {
    const locationName = this.user?.location?.name || 'Restaurant';
    const guestName = party.name || 'Guest';

    if (party.type === PartyTypes.reservation) {
      return this.buildReservationNotificationText(party, locationName, guestName);
    }

    return this.buildWaitlistNotificationText(party, locationName, guestName);
  }

  private buildReservationNotificationText(party: Party, locationName: string, guestName: string): string {
    const timezone = this.user?.location?.timezone || moment.tz.guess();
    const formattedTime = moment.tz(party.quoted_time, timezone).format('MM/DD/YYYY h:mm a');

    const reservationLabelBySource: Partial<Record<PartySource, string>> = {
      [PartySource.public]: 'online reservation',
      [PartySource.google]: 'google reservation',
      [PartySource.api]: 'api reservation',
    };
    const label = reservationLabelBySource[party.source] || 'reservation';

    return `${locationName} received a new ${label} from ${guestName} on ${formattedTime}`;
  }

  private buildWaitlistNotificationText(party: Party, locationName: string, guestName: string): string {
    const configBySource: Partial<Record<PartySource, { label: string; via: string }>> = {
      [PartySource.public]: { label: 'waitlist check-in', via: 'public check-in' },
      [PartySource.sms]: { label: 'waitlist check-in', via: 'sms sign-up' },
      [PartySource.google]: { label: 'google waitlist check-in', via: 'google' },
      [PartySource.api]: { label: 'api waitlist check-in', via: 'api' },
    };
    const config = configBySource[party.source];
    const label = config?.label || 'waitlist check-in';
    const via = config?.via || sourceToStringMap[party.source] || 'unknown';

    return `${locationName} received a new ${label} from ${guestName} via ${via}`;
  }

  getSourceLabel(party: Party): string {
    const sourceMap: Record<string, string> = {
      [PartySource.sms]: 'SMS',
      [PartySource.public]: 'Public Page',
      [PartySource.api]: 'API',
      [PartySource.google]: 'Google',
    };

    return sourceMap[party.source] || sourceToStringMap[party.source] || 'Unknown';
  }

  private getActiveNotificationType(): NotificationType | null {
    if (this.selectedTab === 'messages') {
      return NotificationType.message;
    }

    if (this.selectedTab === 'notifications') {
      return NotificationType.notification;
    }
    return null;
  }

  private initializeCounters(): void {
    this.messagesCount$ = this.notificationCenterService.messagesCount$;
    this.notificationsCount$ = this.notificationCenterService.notificationsCount$;
  }

  private subscribeToAuthUser(): void {
    const authUserSub = this.authRestaurantService.selectWithAccount()
      .pipe(tap((user) => {
        if (!user?.account || !user?.location) {
          return;
        }
        this.user = user;
        this.location = user.location;
        this.isStarterPlan = this.pricePlansRestrictionsService
          .availableForStarterPlan(user.account.subscriptionType, user.location.recurly);
        this.isBusinessPlan = this.pricePlansRestrictionsService
          .availableForBusinessPlan(user.account.subscriptionType, user.location.recurly);

        this.showMessagesTab = this.isStarterPlan || this.isBusinessPlan;
        this.showNotificationsTab = this.isBusinessPlan;

        this.tabs = ['messages'];
        if (this.showNotificationsTab) {
          this.tabs.push('notifications');
        }
        if (!this.tabs.includes(this.selectedTab)) {
          this.selectedTab = this.tabs[0];
        }
      }))
      .subscribe();
    this.subscriptions.add(authUserSub);
  }

  private subscribeToWebSocketEvents(): void {
    const inboundSmsSub = this.socketService
      .getEventStream(WebSocketEventsEnum.InboundSmsEvent)
      .pipe(
        debounceTime(WS_DEBOUNCE_MS),
        switchMap((wsData: unknown) => from(this.menuController.isOpen(MENU_ID))
          .pipe(tap((isOpen) => {
            if (!isOpen) {
              return;
            }

            const party = this.notificationCenterService.extractPartyFromEvent(wsData);

            if (party) {
              this.updatePartyInList(party, NotificationType.message);
            } else {
              this.loadMessages(true);
            }
          }))),
      )
      .subscribe();
    this.subscriptions.add(inboundSmsSub);

    const partiesSub = this.subscribeToNotificationParties(WebSocketEventsEnum.partyCreated);
    this.subscriptions.add(partiesSub);

    // A stripe hold still arrives as partyCreated (the reservations grid needs it) but is not a
    // notification yet; this event brings the same party back once its payment is confirmed.
    const paidSub = this.subscribeToNotificationParties(WebSocketEventsEnum.partyPaymentConfirmed);
    this.subscriptions.add(paidSub);

    const messageReadSub = this.socketService
      .getEventStream(WebSocketEventsEnum.messageRead)
      .subscribe((wsData: unknown) => {
        const partyId = this.notificationCenterService.extractPartyIdFromEvent(wsData);
        if (partyId) {
          this.removePartyFromList(partyId, NotificationType.message);
        }
      });
    this.subscriptions.add(messageReadSub);

    const notificationReadSub = this.socketService
      .getEventStream(WebSocketEventsEnum.notificationRead)
      .subscribe((wsData: unknown) => {
        const partyId = this.notificationCenterService.extractPartyIdFromEvent(wsData);
        if (partyId) {
          this.removePartyFromList(partyId, NotificationType.notification);
        }
      });
    this.subscriptions.add(notificationReadSub);

    const allMessagesReadSub = this.socketService
      .getEventStream(WebSocketEventsEnum.allMessagesRead)
      .subscribe(() => {
        this.clearListForType(NotificationType.message);
      });
    this.subscriptions.add(allMessagesReadSub);

    const allNotificationsReadSub = this.socketService
      .getEventStream(WebSocketEventsEnum.allNotificationsRead)
      .subscribe(() => {
        this.clearListForType(NotificationType.notification);
      });
    this.subscriptions.add(allNotificationsReadSub);
  }

  // partyCreated and partyPaymentConfirmed feed the same notifications list, so they share this
  // handler. `extractPartyFromEvent` copes with both payload shapes: partyCreated arrives from
  // the parties room (envelope already unwrapped by the socket service) and partyPaymentConfirmed
  // from the location room as a plain `[party]` array. A party whose checkout is still unpaid is
  // dropped entirely — no insert and no reload, since a reload would not return it either.
  // `updatePartyInList` is id-deduped, so the confirmation insert is safe even if the list
  // already holds the party.
  private subscribeToNotificationParties(wsEvent: WebSocketEventsEnum): Subscription {
    return this.socketService
      .getEventStream(wsEvent)
      .pipe(
        debounceTime(WS_DEBOUNCE_MS),
        switchMap((wsData: unknown) => from(this.menuController.isOpen(MENU_ID))
          .pipe(tap((isOpen) => {
            if (!isOpen) {
              return;
            }

            const party = this.notificationCenterService.extractPartyFromEvent(wsData);

            if (!party) {
              this.loadNotifications(true);
              return;
            }

            if (!hasUnpaidCheckout(party)) {
              this.updatePartyInList(party, NotificationType.notification);
            }
          }))),
      )
      .subscribe();
  }

  private loadData(type: NotificationType, reset = false): void {
    if (reset) {
      this.resetTypeState(type);
    }

    const config = this.notificationTypeStrategies[type];

    if (!config.getHasMore() || !this.location || config.getIsLoading()) {
      return;
    }

    const currentOffset: number = config.getOffset();
    const locationId: string = this.location._id;
    const emptyList: Party[] = [];
    config.setIsLoading(true);

    const loadSub = config.loadFn(locationId, PAGE_SIZE, currentOffset)
      .pipe(
        catchError(() => of(emptyList)),
        finalize(() => config.setIsLoading(false)),
      )
      .subscribe((parties) => {
        this.applyLoadedParties(parties, type, reset);
      });
    this.subscriptions.add(loadSub);
  }

  private applyLoadedParties(parties: Party[], type: NotificationType, reset: boolean): void {
    const config = this.notificationTypeStrategies[type];

    if (parties.length < PAGE_SIZE) {
      config.setHasMore(false);
    }

    const currentList = config.getList();
    config.setList(reset ? parties : [...currentList, ...parties]);
    config.setOffset(config.getOffset() + parties.length);
  }

  private loadMessages(reset = false): void {
    this.loadData(NotificationType.message, reset);
  }

  private loadNotifications(reset = false): void {
    this.loadData(NotificationType.notification, reset);
  }

  private handleMarkAsRead(
    party: Party,
    type: NotificationType,
    options: { emitEvent?: boolean; removeFromList?: boolean } = {},
  ): void {
    const { emitEvent = false, removeFromList = false } = options;
    const config = this.notificationTypeStrategies[type];

    const markAsReadSub = config.markAsReadFn(party._id)
      .pipe(
        take(1),
        tap(() => {
          if (removeFromList) {
            this.removePartyFromList(party._id, type);
          }
        }),
        catchError(() => of(null)),
        finalize(() => {
          if (emitEvent) {
            config.emitter?.emit(party);
          }
        }),
      )
      .subscribe();

    this.subscriptions.add(markAsReadSub);
  }

  private removePartyFromList(partyId: string, type: NotificationType): void {
    const config = this.notificationTypeStrategies[type];
    config.setList(config.getList().filter(p => p._id !== partyId));
  }

  private handleMarkAllAsRead(type: NotificationType): void {
    if (!this.location) {
      return;
    }

    const config = this.notificationTypeStrategies[type];

    if (config.getList().length === 0) {
      return;
    }

    const markAllSub = config.markAllAsReadFn(this.location._id)
      .pipe(
        take(1),
        tap(() => this.clearListForType(type)),
        catchError(() => of(null)),
      )
      .subscribe();

    this.subscriptions.add(markAllSub);
  }

  private clearListForType(type: NotificationType): void {
    const config = this.notificationTypeStrategies[type];
    config.setList([]);
    config.setOffset(0);
    config.setHasMore(false);
  }

  private resetTypeState(type: NotificationType): void {
    const config = this.notificationTypeStrategies[type];
    config.setOffset(0);
    config.setList([]);
    config.setHasMore(true);
  }

  private resetPaginationState(): void {
    this.resetTypeState(NotificationType.message);
    this.resetTypeState(NotificationType.notification);
  }

  private updatePartyInList(party: Party, type: NotificationType): void {
    const config = this.notificationTypeStrategies[type];
    const list = config.getList();
    const existingIndex = list.findIndex(p => p._id === party._id);
    const notFoundIndex = -1;

    if (existingIndex === notFoundIndex) {
      config.setList([party, ...list]);
    } else {
      const updatedList = [...list];
      updatedList[existingIndex] = {
        ...list[existingIndex],
        ...party,
      };
      config.setList(updatedList);
    }
  }

  private setSidebarWidth(width: number): void {
    this.currentSidebarWidth = width;
  }
}
